import { useState } from 'react';
import { useAnalytics, useInventory, useSites } from '../api/hooks';
import { Empty, ErrorState, Loading } from '../components/States';
import { inr, num } from '../components/money';

export default function Inventory() {
  const sites = useSites();
  const [site, setSite] = useState<string | null>(null);
  const chosen = site ?? sites.data?.[0]?.name ?? null;
  const inv = useInventory(chosen);
  const { data: a } = useAnalytics(30, null);

  if (sites.isPending) return <Loading what="your sites" />;
  if (sites.error) return <ErrorState error={sites.error} onRetry={() => sites.refetch()} />;
  if (!sites.data?.length) return <Empty title="No sites yet">Sites are created in the Android app.</Empty>;

  // Days of cover comes from the analytics payload, which already computes it.
  const cover = new Map((a?.stock_intelligence.stockout_risk ?? [])
    .map((r) => [`${r.site_name}||${r.material_name}`, r]));

  return (
    <>
      <div className="section__head">
        <h1 className="section__title">Inventory</h1>
        <div className="switches" role="group" aria-label="Site">
          {sites.data.map((s) => (
            <button key={s._id} className={`switch${chosen === s.name ? ' switch--on' : ''}`}
              onClick={() => setSite(s.name)} aria-pressed={chosen === s.name}>{s.name}</button>
          ))}
        </div>
      </div>

      {inv.isPending ? <Loading what={chosen ?? 'inventory'} />
        : inv.error ? <ErrorState error={inv.error} onRetry={() => inv.refetch()} />
        : !inv.data?.length ? <Empty title={`${chosen} has no materials yet`} />
        : (
          <div className="card scroll-x" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr><th>Material</th><th>On hand</th><th>Unit cost</th><th>Value</th><th>Low at</th><th>Cover</th></tr>
              </thead>
              <tbody>
                {inv.data.map((i) => {
                  const c = cover.get(`${i.site_name}||${i.name}`);
                  const low = i.quantity <= i.low_stock_threshold;
                  return (
                    <tr key={i._id}>
                      <td><strong>{i.name}</strong></td>
                      <td className="mono-num">
                        {num(i.quantity)} {i.unit}
                        {i.quantity <= 0 ? <span className="pill pill--bad" style={{ marginLeft: 8 }}>out</span>
                          : low ? <span className="pill pill--warn" style={{ marginLeft: 8 }}>low</span> : null}
                      </td>
                      {/* An unpriced material shows a dash, never ₹0. */}
                      <td className="mono-num">{inr(i.unit_cost)}</td>
                      <td className="mono-num">{i.unit_cost === null ? '—' : inr(i.quantity * i.unit_cost)}</td>
                      <td className="mono-num muted">{num(i.low_stock_threshold)}</td>
                      <td className="mono-num">
                        {c?.days_left === null || c?.days_left === undefined ? <span className="muted">—</span>
                          : <span className={`pill ${c.days_left <= 7 ? 'pill--bad' : c.days_left <= 14 ? 'pill--warn' : 'pill--ok'}`}>
                              {c.days_left}d
                            </span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
}
